import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { FinanceTextField } from '../../../../financeProviders/shared/FinanceTextField/FinanceTextField';
import { Label } from '../../../../../Toolkit/Inputs/Label';
import { Spacer } from '../../../../../../helpers/genericStyles';
import { Divider } from '../../../../../ToolkitV2/Divider/Divider';
import { FinanceSelect } from '../../../../financeProviders/shared/FinanceSelect';

import { THRESHOLD_TO_SHOW_PREVIOUS_EMPLOYMENT_FIELDS_IN_YEARS } from './constants';

import { type BankOfIrelandForm } from '../../../../queries/schema/carFinance.schema';

/*
 * We avoid using the same names for constants in each of the employment panels
 * so that sonarcloud does not incorrectly mark these blocks as duplicates.
 */

/*
 * Ideally, these hardcoded values would come from the server.
 *
 * However, the legacy APIs used for car finance do not provide these values.
 * Furthermore, no changes are planned to these APIs unless absolutely necessary.
 *
 * We must therefore wait until these APIs are migrated to the new platform
 * before we may update them to include these values.
 */
const BOI_EMPLOYMENT_STATUS = [
  {
    label: 'Select employment status',
    value: '',
  },
  'Full Time Employee - Public Sector',
  'Full Time Employee - Private Sector',
  'Part Time Employee - Public Sector',
  'Part Time Employee - Private Sector',
  'Self Employed',
  'Unemployed',
  'Student',
  'House Person',
  'Retired',
];

const OCCUPATION = [
  {
    label: 'Select occupation',
    value: '',
  },
  'Accountant',
  'Actor',
  'Actuary',
  'Advertising Executive',
  'Air Steward/Stewardess',
  'Ambassador/Diplomat',
  'Analyst',
  'Architect',
  'Army-Officer',
  'Army-Other',
  'Artist',
  'Assembler',
  'Assembly Line Worker',
  'Assessor',
  'Auctioneer/Valuer',
  'Author',
  'Bailiff',
  'Baker',
  'Bank - Manager',
  'Bank - Official',
  'Barrister/Lawyer',
  'Beautician',
  'Bog Worker',
  'Book-Keeper',
  'Book-Maker',
  'Builder',
  'Business/Company Executive',
  'Butcher',
  'Cardiographer',
  'Caretaker',
  'Carpenter',
  'Cartographer',
  'Cashier',
  'Chef/Cook',
  'Chemist/Biochemist',
  'Childminder',
  'Chiropodist',
  'Civ Servant-Executive',
  'Civ Servant-Junior',
  'Cleaner',
  'Clergyman/Priest',
  'Clerical Worker',
  'Commercial Traveller',
  'Company Director',
  'Company Executive',
  'Company Secretary',
  'Company Operator',
  'Computer Programmer',
  'Construction Worker',
  'Consultant',
  'Consultant-Business',
  'Consultant-Financial',
  'Courier',
  'Craftsman',
  'Curator',
  'Dealer',
  'Dentist',
  'Designer',
  'Detective',
  'Dietician',
  'Doctor/Medical Practitioner',
  'Draughtsman',
  'Dressmaker/Tailor',
  'Driver',
  'Economist',
  'Editor',
  'Electrician',
  'Engineer',
  'Estate Agent',
  'Factory Worker',
  'Farmer',
  'Farm Worker',
  'Financial Controller',
  'Fireman',
  'Fisherman',
  'Fitter',
  'Florist',
  'Foreman',
  'Forester',
  'Garda-Ranked',
  'Garda-Other',
  'Gardener',
  'General Manager',
  'Geologist',
  'Glazier',
  'Hairdresser',
  'Health Inspector',
  'Hospital Orderly',
  'Housekeeper',
  'Houseperson',
  'Industrial Designer',
  'Insurance Agent',
  'Interior Decorator',
  'Jeweller',
  'Journalist',
  'Judge',
  'Laboratory Technician',
  'Labourer',
  'Lecturer',
  'Librarian',
  'Lifeguard',
  'Machinist',
  'Manager-Catering',
  'Manager-Fin. Inst',
  'Managing Director',
  'Mgr-Med Business',
  'Mgr-Small Business',
  'Mathematician',
  'Mechanic',
  'Medical Practitioner',
  'Metal Worker',
  'Messenger',
  'Midwife',
  'Milkman',
  'Miner',
  'Musician',
  'Nurse',
  'Optician',
  'Other Professional',
  'Other Non-Manual',
  'Other Skilled Manual',
  'Other Unskilled Manual',
  'Painter',
  'Panel Beater',
  'Pawnbroker',
  'Pensioner',
  'Personnel Officer',
  'Pharmacist',
  'Photographer',
  'Physiotherapist',
  'Physiologist',
  'Pilot/Navigator',
  'Plasterer',
  'Plumber',
  'Politician',
  'Porter',
  'Postman',
  'Printer',
  'Production Manager',
  'Prop-Large Business',
  'Prop-Small Business',
  'Psychiatrist',
  'Publican',
  'Publisher',
  'Radiographer',
  'Receptionist',
  'Reporter',
  'Registrar',
  'Religious',
  'Researcher',
  'Retired',
  'Sailor/Seaman',
  'Sales Assistant',
  'Salesman',
  'Sales Manager',
  'Scientist',
  'Secretary',
  'Security Officer',
  'Sheetmetal Worker',
  "Ship's Officer",
  'Shoe Maker',
  'Shopkeeper',
  'Social Worker',
  'Solicitor',
  'Stockbroker',
  'Student',
  'Supervisor',
  'Surveyor',
  'Taxi Owner',
  'Teacher-Physical Ed',
  'Teacher-Primary',
  'Teacher-Secondary',
  'Teacher-Principal',
  'Teacher-Vocational',
  'Technician',
  'Telephone Operator',
  'Telephone Technician',
  'Textile Worker',
  'Therapist',
  'Trader',
  'Transport Operator',
  'Typist',
  'Undertaker',
  'Unemployed',
  'Veterinary Surgeon',
  'Waiter/Waitress',
  'Warden',
  'Welder',
  'Youth Employ/Training',
];

const BOI_TIME_AT_EMPLOYER = [
  {
    label: 'Select time',
    value: '',
  },
  { value: '1', label: '1 year' },
  { value: '2', label: '2 years' },
  { value: '3', label: '3 years' },
  { value: '4', label: '4 years' },
  { value: '5', label: '5 years' },
  { value: '6', label: 'More than 5 years' },
];

const EmploymentDetails = () => {
  const [showPreviousEmploymentFields, setShowPreviousEmploymentFields] =
    useState(false);
  const { control, resetField } = useFormContext<BankOfIrelandForm>();

  const handleTimeWithEmployerChange = (timeAtCurrentEmployment: string) => {
    if (
      Number(timeAtCurrentEmployment) >
      THRESHOLD_TO_SHOW_PREVIOUS_EMPLOYMENT_FIELDS_IN_YEARS
    ) {
      resetField('previousEmploymentDetails');
      setShowPreviousEmploymentFields(false);
    } else {
      setShowPreviousEmploymentFields(true);
    }
  };

  return (
    <>
      <Spacer marginBottom={['M16', 'M24']} />
      <Label htmlFor="employmentDetails.status">Employment Status*</Label>
      <Controller
        control={control}
        name="employmentDetails.status"
        render={({ field, fieldState }) => (
          <FinanceSelect
            {...field}
            {...fieldState}
            placeholderAsOption
            options={BOI_EMPLOYMENT_STATUS}
          />
        )}
      />
      <Spacer marginBottom="S4" />
      <Label htmlFor="employmentDetails.occupation">Occupation*</Label>
      <Controller
        control={control}
        name="employmentDetails.occupation"
        render={({ field, fieldState }) => (
          <FinanceSelect
            {...field}
            {...fieldState}
            placeholderAsOption
            options={OCCUPATION}
          />
        )}
      />
      <Spacer marginBottom="S4" />
      <Label htmlFor="employmentDetails.employerName">Employer Name*</Label>
      <Controller
        control={control}
        name="employmentDetails.employerName"
        render={({ field, fieldState }) => (
          <FinanceTextField
            {...field}
            {...fieldState}
            placeholder="Enter employer name"
          />
        )}
      />
      <Spacer marginBottom="S4" />
      <Label htmlFor="employmentDetails.timeWithEmployer.years">
        Time at Current Employer*
      </Label>
      <Controller
        control={control}
        name="employmentDetails.timeWithEmployer.years"
        render={({ field, fieldState }) => (
          <FinanceSelect
            {...field}
            {...fieldState}
            onChange={(selected) => {
              field.onChange(selected);
              handleTimeWithEmployerChange(selected);
            }}
            placeholderAsOption
            options={BOI_TIME_AT_EMPLOYER}
          />
        )}
      />
      {showPreviousEmploymentFields && (
        <>
          <Spacer marginBottom={'S12'} />
          <Divider />
          <Spacer marginBottom={'M32'} />
          <Label htmlFor="previousEmploymentDetails.status">
            Previous Employment Status*
          </Label>
          <Controller
            control={control}
            name="previousEmploymentDetails.status"
            render={({ field, fieldState }) => (
              <FinanceSelect
                {...field}
                {...fieldState}
                placeholderAsOption
                options={BOI_EMPLOYMENT_STATUS}
              />
            )}
          />
          <Spacer marginBottom="S4" />
          <Label htmlFor="previousEmploymentDetails.previousOccupation">
            Previous Occupation*
          </Label>
          <Controller
            control={control}
            name="previousEmploymentDetails.previousOccupation"
            render={({ field, fieldState }) => (
              <FinanceSelect
                {...field}
                {...fieldState}
                placeholderAsOption
                options={OCCUPATION}
              />
            )}
          />
          <Spacer marginBottom="S4" />
          <Label htmlFor="previousEmploymentDetails.previousEmployerName">
            Previous Employer Name*
          </Label>
          <Controller
            control={control}
            name="previousEmploymentDetails.previousEmployerName"
            render={({ field, fieldState }) => (
              <FinanceTextField
                {...field}
                {...fieldState}
                placeholder="Enter employer name"
              />
            )}
          />
          <Spacer marginBottom="S4" />
          <Label htmlFor="previousEmploymentDetails.timeWithPreviousEmployer.years">
            Time at Previous Employer*
          </Label>
          <Controller
            control={control}
            name="previousEmploymentDetails.timeWithPreviousEmployer.years"
            render={({ field, fieldState }) => (
              <FinanceSelect
                {...field}
                {...fieldState}
                placeholderAsOption
                options={BOI_TIME_AT_EMPLOYER}
              />
            )}
          />
        </>
      )}
    </>
  );
};

export { EmploymentDetails };
